import { getFormattedDate } from '@app/utils/date'
import { dateFormat } from '@app/constants'

import { CreateMonitoringTaskDto } from '@app/api/routes/monitoringTasks/dtos'

import { isNullable } from '@app/utils'

import type { TableData } from '@app/types/table'
import type { ResponseDto } from '@app/api/dtos'
import type { CreateAllTasksData } from '@app/api/routes/monitoringTasks/types'
import type { RegularMonitoringDataSource } from '../types'
import type { MonitoringListCreatingTasksDto } from '@app/api/routes/monitoringLists/dtos'
import type { DecodedValueMap } from 'use-query-params'
import type { QueryParamConfigMap } from 'serialize-query-params'

export const adaptRegularMonitoringTableData = (
  monitoringListCreatingTasksResponse: ResponseDto<MonitoringListCreatingTasksDto>
): TableData<RegularMonitoringDataSource> => {
  const { count, results: monitoringListCreatingTasks } = monitoringListCreatingTasksResponse

  const dataSource: RegularMonitoringDataSource[] = monitoringListCreatingTasks.map(dto => ({
    key: dto.runParametersId,
    monitoringListId: dto.monitoringListId,
    monitoringType: dto.monitoringType?.name ?? null,
    subtype: dto.subtype?.name ?? null,
    promotionName: dto.promo?.promotionNumber ?? null,
    promoDateFrom: getFormattedDate(dto.promo?.dateFrom, dateFormat.DD_MM_YYYY),
    promoDateTo: getFormattedDate(dto.promo?.dateTo, dateFormat.DD_MM_YYYY),
    subtypeId: dto.subtype?.id ?? null,
    cluster: dto.cluster?.name ?? null,
    clusterId: dto.cluster?.id ?? null,
    clustersGroup: dto.cluster?.name ?? null,
    store: dto.store?.name ?? null,
    contractor: dto.contractor?.name ?? null,
    contractorId: dto.contractor?.userId ?? null,
    competitorIds: dto.competitors?.map(competitor => competitor.id) ?? null,
    competitorsCount: dto.competitors?.length,
    isLaunchAccess: dto.isLaunchAccess,
    startAt: getFormattedDate(dto.schedule?.monitoringStartAtDatetime, dateFormat.DD_MM_YYYY),
    gettingDateAt: getFormattedDate(dto.schedule?.receivingDataDeadlineDatetime, dateFormat.DD_MM_YYYY_HH_mm),
    processingDateAt: getFormattedDate(dto.schedule?.processingDataDeadlineDatetime, dateFormat.DD_MM_YYYY_HH_mm),
    dateDeadline: getFormattedDate(dto.schedule?.newDataDeadlineDatetime, dateFormat.DD_MM_YYYY_HH_mm)
  }))

  return { count, dataSource }
}

export const adaptCreateAllTasksData = (queryParams: DecodedValueMap<QueryParamConfigMap>): CreateAllTasksData => {
  return {
    monitoringTypeIds: queryParams.monitoringTypeIds,
    excludedMonitoringTypeIds: queryParams.excludedMonitoringTypeIds,
    monitoringSubtypeIds: queryParams.monitoringSubtypeIds,
    excludeSubtypeIds: queryParams.excludedMonitoringSubtypeIds,
    clusterIds: queryParams.clusterIds,
    excludedClusterIds: queryParams.excludedClusterIds,
    salesOrganizationIds: queryParams.salesOrganizationIds,
    excludedSalesOrganizationIds: queryParams.excludedSalesOrganizationIds,
    storeIds: queryParams.storeIds,
    excludedStoreIds: queryParams.excludedStoreIds,
    excludedPromoIds: queryParams.excludedPromoIds
  }
}

export const adaptCreateMonitoringTask = (
  dataSource?: RegularMonitoringDataSource[]
): CreateMonitoringTaskDto[] | undefined => {
  if (isNullable(dataSource)) return

  return dataSource.map(record => ({
    initiator: null,
    contractorId: record.contractorId,
    clusterId: record.clusterId,
    monitoringSubtypeId: record.subtypeId,
    monitoringListId: record.monitoringListId,
    competitorIds: record.competitorIds,
    monitoringStartAt: getFormattedDate(record.startAt, dateFormat.YYYY_MM_DDTHH_mm_ss, {
      extractionFormat: dateFormat.DD_MM_YYYY
    }),
    receivingDataDeadlineAt: getFormattedDate(record.gettingDateAt, dateFormat.YYYY_MM_DDTHH_mm_ss, {
      extractionFormat: dateFormat.DD_MM_YYYY_HH_mm
    }),
    processingDataDeadlineAt: getFormattedDate(record.processingDateAt, dateFormat.YYYY_MM_DDTHH_mm_ss, {
      extractionFormat: dateFormat.DD_MM_YYYY_HH_mm
    }),
    newDateDeadlineAt: getFormattedDate(record.dateDeadline, dateFormat.YYYY_MM_DDTHH_mm_ss, {
      extractionFormat: dateFormat.DD_MM_YYYY_HH_mm
    })
  }))
}
